import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, MessageSquare, BarChart3, List, TrendingUp, Award } from 'lucide-react'
import { MoodEntry } from '../types'
import { Button } from './ui/button'

interface HistoryProps {
  entries: MoodEntry[]
}

const moodLabels = {
  1: 'Molto Male',
  2: 'Male',
  3: 'Neutro', 
  4: 'Bene',
  5: 'Molto Bene'
}

const moodColors = {
  1: 'bg-red-500',
  2: 'bg-orange-500',
  3: 'bg-yellow-500',
  4: 'bg-purple-500',
  5: 'bg-purple-500'
}

const moodEmojis = {
  1: '😢',
  2: '😕',
  3: '😐',
  4: '😊',
  5: '🥰'
}

export default function History({ entries }: HistoryProps) {
  const [viewMode, setViewMode] = useState<'list' | 'chart'>('list')

  const sortedEntries = [...entries].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Oggi'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ieri'
    } else {
      return date.toLocaleDateString('it-IT', { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short' 
      })
    }
  }

  const getAverageMood = () => {
    if (entries.length === 0) return 0
    const sum = entries.reduce((acc, entry) => acc + entry.mood, 0)
    return (sum / entries.length).toFixed(1)
  }

  const getStreak = () => {
    let streak = 0
    const today = new Date()
    
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(today)
      checkDate.setDate(today.getDate() - i)
      const hasEntry = entries.some(entry => 
        new Date(entry.date).toDateString() === checkDate.toDateString()
      )
      
      if (hasEntry) {
        streak++
      } else if (i > 0) {
        break
      }
    }
    
    return streak
  }

  if (entries.length === 0) {
    return (
      <motion.div 
        className="text-center py-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 4, 
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Calendar className="w-20 h-20 text-gray-300 mx-auto mb-6" />
        </motion.div>
        <h3 className="text-xl font-bold text-gray-600 mb-3">
          Il Tuo Viaggio Inizia Qui ✨
        </h3>
        <p className="text-gray-500 max-w-sm mx-auto leading-relaxed">
          Inizia a tracciare il tuo umore quotidiano per vedere la magia del tuo percorso emotivo
        </p>
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <motion.div 
        className="grid grid-cols-3 gap-3"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl text-center border border-purple-200 shadow-lg"
          whileHover={{ scale: 1.05 }}
        >
          <div className="text-2xl font-bold text-purple-600">{entries.length}</div>
          <div className="text-xs text-purple-500 font-medium">Momenti</div>
        </motion.div>
        
        <motion.div 
          className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl text-center border border-purple-200 shadow-lg"
          whileHover={{ scale: 1.05 }}
        >
          <div className="text-2xl font-bold text-purple-600">{getAverageMood()}</div>
          <div className="text-xs text-purple-500 font-medium">Media</div>
        </motion.div>
        
        <motion.div 
          className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-2xl text-center border border-orange-200 shadow-lg"
          whileHover={{ scale: 1.05 }}
        >
          <div className="text-2xl font-bold text-orange-600">{getStreak()}</div>
          <div className="text-xs text-orange-500 font-medium">Streak</div>
        </motion.div>
      </motion.div>

      {/* View Toggle */}
      <motion.div 
        className="flex space-x-2 bg-gray-100 p-1 rounded-2xl"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <motion.button
          onClick={() => setViewMode('list')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
            viewMode === 'list' 
              ? 'bg-white text-purple-600 shadow-lg' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <List className="w-4 h-4 mr-2 inline" />
          Lista
        </motion.button>
        <motion.button
          onClick={() => setViewMode('chart')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
            viewMode === 'chart' 
              ? 'bg-white text-purple-600 shadow-lg' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <BarChart3 className="w-4 h-4 mr-2 inline" />
          Grafico
        </motion.button>
      </motion.div>

      <AnimatePresence mode="wait">
        {viewMode === 'list' && (
          <motion.div
            key="list"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {sortedEntries.map((entry, index) => (
              <motion.div
                key={entry.id}
                className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -2 }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-4">
                    <motion.div 
                      className={`w-4 h-4 rounded-full ${moodColors[entry.mood as keyof typeof moodColors]} shadow-lg`}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: index * 0.2 }}
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-bold text-gray-800 text-lg">
                          {moodLabels[entry.mood as keyof typeof moodLabels]}
                        </h4>
                        <span className="text-2xl">
                          {moodEmojis[entry.mood as keyof typeof moodEmojis]}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 font-medium">
                        {formatDate(entry.date)}
                      </p>
                    </div>
                  </div>
                  <motion.div 
                    className="text-3xl font-bold text-gray-300"
                    whileHover={{ scale: 1.2, color: '#6366f1' }}
                  >
                    {entry.mood}
                  </motion.div>
                </div>
                
                {entry.note && (
                  <motion.div 
                    className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="flex items-start space-x-3">
                      <MessageSquare className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700 leading-relaxed">{entry.note}</p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {viewMode === 'chart' && (
          <motion.div
            key="chart"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </motion.div>
                <h3 className="font-bold text-gray-800 text-lg">Andamento Umore</h3>
              </div>
              
              <div className="space-y-3">
                {sortedEntries.slice(0, 14).reverse().map((entry, index) => (
                  <motion.div 
                    key={entry.id} 
                    className="flex items-center space-x-4"
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-16 text-sm text-gray-500 font-medium">
                      {formatDate(entry.date)}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden shadow-inner">
                      <motion.div
                        className={`h-full rounded-full ${moodColors[entry.mood as keyof typeof moodColors]} shadow-lg`}
                        initial={{ width: 0 }}
                        animate={{ width: `${(entry.mood / 5) * 100}%` }}
                        transition={{ 
                          duration: 1,
                          delay: index * 0.1,
                          ease: "easeOut"
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-between px-3">
                        <span className="text-sm font-bold text-white drop-shadow-lg">
                          {moodEmojis[entry.mood as keyof typeof moodEmojis]}
                        </span>
                        <span className="text-sm font-bold text-white drop-shadow-lg">
                          {entry.mood}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {getStreak() > 3 && (
              <motion.div
                className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-4 shadow-lg"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center space-x-3">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Award className="w-6 h-6 text-yellow-600" />
                  </motion.div>
                  <div>
                    <h4 className="font-bold text-yellow-800">
                      Streak Fantastico! 🔥
                    </h4>
                    <p className="text-yellow-700 text-sm">
                      {getStreak()} giorni consecutivi di tracking!
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}